/**
 * YouTube Video URL Utilities
 * Handles various YouTube URL formats and provides secure embed URLs
 */

export interface VideoInfo {
  id: string
  platform: 'youtube' | 'vimeo' | 'googledrive' | 'direct'
  embedUrl: string
  thumbnailUrl?: string
}

/**
 * Extract YouTube video ID from various URL formats
 */
export function getYouTubeVideoId(url: string): string | null {
  if (!url) return null
  
  const patterns = [
    // Standard YouTube URLs
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    // YouTube URLs with additional parameters
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    // YouTube Shorts
    /youtube\.com\/shorts\/([^&\n?#]+)/,
    // YouTube live URLs
    /youtube\.com\/live\/([^&\n?#]+)/
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }
  
  return null
}

/**
 * Extract Vimeo video ID from URL
 */
export function getVimeoVideoId(url: string): string | null {
  if (!url) return null
  
  const patterns = [
    /vimeo\.com\/(\d+)/,
    /vimeo\.com\/channels\/\w+\/(\d+)/,
    /vimeo\.com\/groups\/\w+\/videos\/(\d+)/,
    /player\.vimeo\.com\/video\/(\d+)/
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }
  
  return null
}

/**
 * Extract Google Drive video ID from URL and convert to embeddable format
 */
export function getGoogleDriveVideoId(url: string): string | null {
  if (!url) return null
  
  const patterns = [
    // Standard Google Drive share URL
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view/,
    // Direct Google Drive URL
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
    // Google Drive open URL
    /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/,
    // Already processed embed URL
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/preview/
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }
  
  return null
}

/**
 * Create Google Drive embed URL
 */
export function createGoogleDriveEmbedUrl(fileId: string): string {
  return `https://drive.google.com/file/d/${fileId}/preview`
}

/**
 * Create secure YouTube embed URL
 */
export function createYouTubeEmbedUrl(videoId: string, options: {
  autoplay?: boolean
  mute?: boolean
  controls?: boolean
  modestbranding?: boolean
  rel?: boolean
  startTime?: number
} = {}): string {
  const {
    autoplay = false,
    mute = false,
    controls = true,
    modestbranding = true,
    rel = false,
    startTime
  } = options
  
  const params = new URLSearchParams({
    enablejsapi: '1',
    origin: window.location.origin,
    rel: rel ? '1' : '0',
    modestbranding: modestbranding ? '1' : '0',
    controls: controls ? '1' : '0',
    fs: '1',
    cc_load_policy: '0',
    iv_load_policy: '3',
    autohide: '1'
  })
  
  if (autoplay) {
    params.append('autoplay', '1')
  }
  
  if (mute) {
    params.append('mute', '1')
  }
  
  if (startTime) {
    params.append('start', startTime.toString())
  }
  
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`
}

/**
 * Create secure Vimeo embed URL
 */
export function createVimeoEmbedUrl(videoId: string, options: {
  autoplay?: boolean
  muted?: boolean
  title?: boolean
  byline?: boolean
  portrait?: boolean
} = {}): string {
  const {
    autoplay = false,
    muted = false,
    title = false,
    byline = false,
    portrait = false
  } = options
  
  const params = new URLSearchParams({
    title: title ? '1' : '0',
    byline: byline ? '1' : '0',
    portrait: portrait ? '1' : '0',
    badge: '0',
    autopause: '0',
    player_id: '0',
    app_id: '122963'
  })
  
  if (autoplay) {
    params.append('autoplay', '1')
  }
  
  if (muted) {
    params.append('muted', '1')
  }
  
  return `https://player.vimeo.com/video/${videoId}?${params.toString()}`
}

/**
 * Get video platform from URL
 */
export function getVideoPlatform(url: string): 'youtube' | 'vimeo' | 'googledrive' | 'direct' {
  if (!url) return 'direct'
  
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    return 'youtube'
  }
  
  if (url.includes('vimeo.com')) {
    return 'vimeo'
  }
  
  if (url.includes('drive.google.com')) {
    return 'googledrive'
  }
  
  return 'direct'
}

/**
 * Get comprehensive video information from URL
 */
export function getVideoInfo(url: string): VideoInfo | null {
  if (!url) return null
  
  const platform = getVideoPlatform(url)
  
  switch (platform) {
    case 'youtube': {
      const id = getYouTubeVideoId(url)
      if (!id) return null
      
      return {
        id,
        platform,
        embedUrl: createYouTubeEmbedUrl(id),
        thumbnailUrl: `https://img.youtube.com/vi/${id}/maxresdefault.jpg`
      }
    }
    
    case 'vimeo': {
      const id = getVimeoVideoId(url)
      if (!id) return null
      
      return {
        id,
        platform,
        embedUrl: createVimeoEmbedUrl(id)
      }
    }
    
    case 'googledrive': {
      const id = getGoogleDriveVideoId(url)
      if (!id) return null
      
      return {
        id,
        platform,
        embedUrl: createGoogleDriveEmbedUrl(id)
      }
    }
    
    case 'direct': {
      return {
        id: url,
        platform,
        embedUrl: url
      }
    }
    
    default:
      return null
  }
}

/**
 * Get YouTube thumbnail URL
 */
export function getYouTubeThumbnail(videoId: string, quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'maxres'): string {
  const qualityMap = {
    default: 'default.jpg',
    medium: 'mqdefault.jpg',
    high: 'hqdefault.jpg',
    standard: 'sddefault.jpg',
    maxres: 'maxresdefault.jpg'
  }
  
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}`
}

/**
 * Validate video URL
 */
export function isValidVideoUrl(url: string): boolean {
  if (!url) return false
  
  const platform = getVideoPlatform(url)
  
  switch (platform) {
    case 'youtube':
      return !!getYouTubeVideoId(url)
    case 'vimeo':
      return !!getVimeoVideoId(url)
    case 'googledrive':
      return !!getGoogleDriveVideoId(url)
    case 'direct':
      return url.startsWith('http') || url.startsWith('/')
    default:
      return false
  }
}

/**
 * Convert watch URL to embed URL (legacy support)
 */
export function convertToEmbedUrl(url: string): string {
  const videoInfo = getVideoInfo(url)
  return videoInfo?.embedUrl || url
}

/**
 * Check if Google Drive URL is properly formatted for public sharing
 */
export function isGoogleDrivePubliclyShared(url: string): boolean {
  // Check if URL contains sharing indicators
  return url.includes('/view') || url.includes('usp=sharing') || url.includes('usp=drive_link')
}

/**
 * Get instructions for making Google Drive video publicly accessible
 */
export function getGoogleDriveInstructions(): string[] {
  return [
    "1. Open your Google Drive file",
    "2. Right-click the video file and select 'Share'",
    "3. Click 'Change to anyone with the link'",
    "4. Set permission to 'Viewer'",
    "5. Click 'Copy link' and use that URL",
    "6. Make sure the URL contains '/view' at the end"
  ]
}

/**
 * Fix common Google Drive URL issues
 */
export function fixGoogleDriveUrl(url: string): string {
  // If it's already a proper Google Drive URL, return as is
  if (getGoogleDriveVideoId(url)) {
    const fileId = getGoogleDriveVideoId(url)
    return createGoogleDriveEmbedUrl(fileId!)
  }
  
  return url
}
